import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { Reveal } from "./Reveal";
import { DOCS, VIEW_ONLY_DOCS } from "../lib/site";
import { useT } from "../context/LangContext";
import { FilePdf, Eye, ShieldCheck, DownloadSimple, X } from "@phosphor-icons/react";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const PILLAR_ICONS = [Eye, ShieldCheck, FilePdf];

const PdfViewer = ({ src, title, onClose, closeLabel }) => {
  const [pages, setPages] = useState(0);
  const width = Math.min(900, window.innerWidth - 48);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/90 p-3 md:p-8" onClick={onClose} data-testid="pdf-viewer" role="dialog" aria-modal="true" onContextMenu={(e) => e.preventDefault()}>
      <div className="relative flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-bone" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <span className="font-display font-bold text-ink">{title}{pages ? <span className="ml-3 text-xs font-normal text-stone">{pages} pag.</span> : null}</span>
          <button onClick={onClose} aria-label={closeLabel} data-testid="pdf-viewer-close" className="rounded-full p-2 text-ink transition-colors hover:bg-sand"><X size={20} weight="bold" /></button>
        </div>
        <div className="flex-1 overflow-y-auto bg-sand/60 p-4 md:p-8 select-none" data-testid="pdf-viewer-pages">
          <Document file={src} onLoadSuccess={({ numPages }) => setPages(numPages)} loading={<div className="py-20 text-center text-sm text-stone">...</div>} className="mx-auto flex flex-col items-center gap-6">
            {Array.from({ length: pages }, (_, i) => (
              <Page key={i} pageNumber={i + 1} width={width} renderTextLayer={false} renderAnnotationLayer={false} className="shadow-lg" />
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
};

export const TransparencySection = () => {
  const { t } = useT();
  const c = t.transparency;
  const [viewing, setViewing] = useState(null);
  return (
    <div className="mt-12" data-testid="transparency-section">
      {viewing && <PdfViewer src={DOCS[viewing.file]} title={viewing.title} onClose={() => setViewing(null)} closeLabel={t.gallery.closeLabel} />}
      <Reveal>
        <p className="font-display font-bold tracking-widest text-xs text-terracotta mb-4">{c.heroKicker}</p>
        <h2 className="font-display font-extrabold tracking-tighter text-3xl md:text-5xl text-ink">{c.heroTitleLines.join(" ")}</h2>
        <p className="mt-5 max-w-2xl text-stone leading-relaxed">{c.heroIntro}</p>
      </Reveal>

      <section className="mt-8" data-testid="documents-section">
        <Reveal className="mb-6">
          <h2 className="font-display font-extrabold tracking-tighter text-3xl md:text-4xl text-ink">{c.docsTitle}</h2>
        </Reveal>
        <div className="space-y-5">
          {c.documents.map((doc, i) => (
            <Reveal key={doc.title} delay={i * 0.08}>
              <div className="group flex flex-col gap-6 rounded-2xl border border-border bg-surface p-6 md:flex-row md:items-center md:justify-between md:p-8 transition-colors duration-300 hover:border-terracotta/50">
                <div className="flex items-start gap-5">
                  <div className="rounded-xl bg-terracotta/10 p-4 shrink-0">
                    <FilePdf size={34} weight="duotone" className="text-terracotta" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display font-bold tracking-tight text-xl text-ink">{doc.title}</h3>
                      <span className="rounded-full bg-sand px-2.5 py-0.5 text-xs font-semibold text-stone">{doc.meta}</span>
                    </div>
                    <p className="mt-2 max-w-xl text-sm text-stone leading-relaxed">{doc.desc}</p>
                  </div>
                </div>
                <div className="flex gap-3 shrink-0">
                  {VIEW_ONLY_DOCS.includes(doc.file) ? (
                    <button type="button" onClick={() => setViewing(doc)} data-testid={`view-doc-${i}`} className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-bone transition-colors duration-200 hover:bg-terracotta">
                      <Eye size={17} /> {c.viewLabel}
                    </button>
                  ) : (
                    <>
                      <a href={DOCS[doc.file]} target="_blank" rel="noopener noreferrer" data-testid={`view-doc-${i}`} className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-ink hover:text-bone">
                        <Eye size={17} /> {c.viewLabel}
                      </a>
                      <a href={DOCS[doc.file]} download data-testid={`download-doc-${i}`} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark">
                        <DownloadSimple size={17} /> {c.downloadLabel}
                      </a>
                    </>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-8 rounded-[2rem] bg-sand p-6 md:p-10" data-testid="transparency-pillars">
        <div className="grid gap-6 md:grid-cols-3">
          {c.pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-border bg-bone p-8">
                  <Icon size={38} weight="duotone" className="text-terracotta mb-5" />
                  <h3 className="font-display font-bold tracking-tight text-xl text-ink mb-3">{p.title}</h3>
                  <p className="text-sm text-stone leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
};
